// src/modules/app-views/_apps/appmulti/orders/pages/Document/tabs/VoucherTab/modals/AddVoucherModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState } from 'react';
import { App, Button, DatePicker, Form, Input, Modal, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import axiosService from 'services/axiosService';
import attachmentsService from 'services/attachmentsService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const STYLES = {
  datePicker: { width: '100%' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AddVoucherModal = ({ open, passenger, order, onClose, onSaved }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { message } = App.useApp();
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [uploaded, setUploaded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const beforeUpload = file => {
    if (file.type !== 'application/pdf') {
      message.error(translateX('only_pdf_allowed'));
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > 6) {
      message.error(translateX('file_too_large'));
      return Upload.LIST_IGNORE;
    }
    (async () => {
      setUploading(true);
      try {
        const result = await attachmentsService.upload({ file, generateThumb: false });
        setUploaded({
          storageUrl: result?.attachment?.storageUrl || result?.storageUrl,
          attachmentId: result?.attachment?._id || result?._id,
          originalName: result?.attachment?.originalName || file.name,
        });
      } catch {
        message.error(translateX('upload_failed'));
      } finally {
        setUploading(false);
      }
    })();
    return Upload.LIST_IGNORE;
  };

  const onOk = async () => {
    const values = await form.validateFields();
    if (!uploaded?.storageUrl) {
      message.error(translateX('attach_voucher_required'));
      return;
    }

    const travellers = (order?.travellers || []).map(t =>
      String(t._id) === String(passenger?._id)
        ? {
            ...t,
            voucherNumber: values.voucherNumber,
            voucherDate: values.voucherDate,
            voucherUrl: uploaded.storageUrl,
          }
        : t,
    );
    const existingIds = (order?.__attachments || []).map(a => a._id);
    const attachmentIds = [...existingIds, uploaded.attachmentId].filter(Boolean);

    setSaving(true);
    try {
      await axiosService.put({
        url: API.APPMULTI_ORDER.DETAIL.replace(':_id', order._id),
        data: { travellers, attachmentIds },
      });
      message.success(translateX('document_updated'));
      onSaved?.();
    } catch {
      message.error(translateX('save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setUploaded(null);
    onClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={translateX('add_voucher')}
      open={open}
      onCancel={handleCancel}
      onOk={onOk}
      confirmLoading={saving}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item label={translateX('passenger')}>
          <Input value={passenger?.name} disabled />
        </Form.Item>
        <Form.Item
          name="voucherNumber"
          label={translateX('voucher_number')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="voucherDate"
          label={translateX('voucher_date')}
          rules={[{ required: true }]}
        >
          <DatePicker style={STYLES.datePicker} />
        </Form.Item>
        <Form.Item label={translateX('voucher_pdf')} required>
          <Upload
            accept=".pdf"
            maxCount={1}
            beforeUpload={beforeUpload}
            showUploadList={{ showPreviewIcon: false }}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              {uploaded?.originalName || translateX('upload_pdf')}
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AddVoucherModal;
